<template>
  <div>
    <!-- Page Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Overview of your AI agents and customer interactions
      </p>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
      <!-- Total Conversations -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Conversations</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.totalConversations || 0 }}</p>
            <p class="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
              <span class="font-medium">+12.5%</span> from last month
            </p>
          </div>
          <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Active Agents -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Active Agents</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.activeAgents || 0 }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              <span class="font-medium">{{ stats.maxAgents || 5 }}</span> max allowed
            </p>
          </div>
          <div class="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M3 13l3-8h12l3 8M3 13v4a2 2 0 002 2h14a2 2 0 002-2v-4"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Open Tickets -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Open Tickets</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.openTickets || 0 }}</p>
            <p class="text-xs text-amber-600 dark:text-amber-400 mt-2">
              <span class="font-medium">{{ stats.pendingTickets || 0 }}</span> pending
            </p>
          </div>
          <div class="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Response Time -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Avg Response Time</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white">{{ stats.avgResponseTime || '2.3s' }}</p>
            <p class="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
              <span class="font-medium">-0.4s</span> from last week
            </p>
          </div>
          <div class="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Recent Activity -->
      <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Latest conversations and support ticket updates
              </p>
            </div>
            <button class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
              View all
            </button>
          </div>
        </div>

        <div class="divide-y divide-gray-200 dark:divide-gray-700">
          <div
            v-for="activity in recentActivity"
            :key="activity.id"
            class="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
          >
            <div class="flex items-start space-x-4">
              <div class="flex-shrink-0">
                <div class="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <span class="text-sm font-semibold text-gray-600 dark:text-gray-300">
                    {{ activity.customer.charAt(0).toUpperCase() }}
                  </span>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ activity.customer }}
                  </p>
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ activity.time }}</span>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {{ activity.message }}
                </p>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                  :class="{
                    'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200': activity.type === 'conversation',
                    'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200': activity.type === 'ticket'
                  }"
                >
                  {{ activity.type === 'conversation' ? 'Conversation' : 'Ticket' }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="recentActivity.length === 0" class="px-6 py-12 text-center">
            <div class="w-12 h-12 mx-auto mb-4 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
            </div>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">No activity</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">Get started by creating your first AI agent.</p>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Quick Actions -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
          </div>
          <div class="p-6 space-y-3">
            <router-link
              to="/agents/new"
              class="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-800"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                </div>
                <span class="text-sm font-semibold text-blue-900 dark:text-blue-100">Create Agent</span>
              </div>
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </router-link>

            <button
              class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                </div>
                <span class="text-sm font-semibold text-gray-900 dark:text-white">Settings</span>
              </div>
              <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Subscription -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Subscription</h3>
          </div>
          <div class="p-6">
            <div class="flex items-center justify-between mb-4">
              <span class="text-sm text-gray-600 dark:text-gray-400">Status</span>
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                :class="{
                  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200': stats.subscriptionStatus === 'active',
                  'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200': stats.subscriptionStatus === 'trial',
                  'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200': stats.subscriptionStatus === 'expired'
                }"
              >
                {{ stats.subscriptionStatus === 'active' ? 'Active' : stats.subscriptionStatus === 'trial' ? 'Trial' : 'Expired' }}
              </span>
            </div>
            <div v-if="stats.subscriptionStatus === 'trial'" class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 mb-4">
              <p class="text-sm text-amber-800 dark:text-amber-200">
                <span class="font-semibold">Trial expires:</span> {{ stats.trialEndDate || '2 days' }}
              </p>
            </div>
            <button
              v-if="stats.subscriptionStatus === 'trial'"
              class="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

interface Stats {
  totalConversations: number
  activeAgents: number
  openTickets: number
  pendingTickets: number
  avgResponseTime: string
  maxAgents: number
  subscriptionStatus: 'active' | 'trial' | 'expired'
  trialEndDate?: string
}

interface Activity {
  id: string
  customer: string
  message: string
  time: string
  type: 'conversation' | 'ticket'
}

const stats = ref<Stats>({
  totalConversations: 0,
  activeAgents: 0,
  openTickets: 0,
  pendingTickets: 0,
  avgResponseTime: '2.3s',
  maxAgents: 5,
  subscriptionStatus: 'trial',
  trialEndDate: '2 days'
})

const recentActivity = ref<Activity[]>([
  {
    id: '1',
    customer: 'John Doe',
    message: 'Started a new conversation about product pricing',
    time: '2 min ago',
    type: 'conversation'
  },
  {
    id: '2',
    customer: 'Jane Smith',
    message: 'Submitted a support ticket for billing issue',
    time: '5 min ago',
    type: 'ticket'
  },
  {
    id: '3',
    customer: 'Bob Johnson',
    message: 'Asked about shipping times for order #12345',
    time: '10 min ago',
    type: 'conversation'
  }
])

onMounted(async () => {
  setTimeout(() => {
    stats.value = {
      totalConversations: 147,
      activeAgents: 3,
      openTickets: 12,
      pendingTickets: 5,
      avgResponseTime: '2.3s',
      maxAgents: 5,
      subscriptionStatus: 'trial',
      trialEndDate: '2 days'
    }
  }, 1000)
})
</script>